import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  Collapse,
  IconButton,
  Select,
  MenuItem,
  LinearProgress,
  Chip,
  styled
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Camera as CameraIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  EmojiEvents as RewardIcon
} from '@mui/icons-material';
import Tesseract from 'tesseract.js';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#00BCD4',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    '&.Mui-focused': {
      color: '#00BCD4',
    },
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(3),
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  '& .MuiLinearProgress-bar': {
    backgroundColor: '#00BCD4',
  },
}));

const ExpenseRequestFormPro = ({ onSubmit, projects, isSubmitting }) => {
  // State declarations
  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    project: '',
    projectMission: '',
    dateSubmitted: new Date().toISOString().substring(0, 10),
    attachments: [],
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [progress, setProgress] = useState(0);
  const [rewards, setRewards] = useState([]);
  const [cameraImage, setCameraImage] = useState(null);
  const [ocrResult, setOcrResult] = useState('');

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle project selection
  const handleProjectChange = (e) => {
    const selectedProject = e.target.value;
    const projectObj = projects.find(proj => proj._id === selectedProject);
    setFormData(prev => ({
      ...prev,
      project: selectedProject,
      projectMission: projectObj?.mission || 'No mission available'
    }));
  };

  // Handle file attachments
  const handleAttachmentChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
  };

  // Handle camera capture
  const handleCameraCapture = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCameraImage(reader.result);
        performOCR(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Perform OCR on captured image
  const performOCR = async (image) => {
    try {
      const { data: { text } } = await Tesseract.recognize(image, 'eng');
      setOcrResult(text);
      autoFillFields(text);
    } catch (err) {
      setError('OCR processing failed: ' + err.message);
    }
  };

  // Auto-fill form fields from OCR result
  const autoFillFields = (text) => {
    const lines = text.split('\n');
    lines.forEach(line => {
      if (line.includes('$')) {
        const extractedAmount = line.match(/(\$[\d,]+)/);
        if (extractedAmount) {
          setFormData(prev => ({
            ...prev,
            amount: extractedAmount[0].replace(/[^0-9.-]+/g, '')
          }));
        }
      }
      if (line.includes('Description:')) {
        setFormData(prev => ({
          ...prev,
          description: line.split(':')[1]?.trim() || ''
        }));
      }
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onSubmit(formData);
      setSuccess('Expense request submitted successfully!');
      setProgress(prev => Math.min(prev + 10, 100));
      if (progress >= 100) {
        setRewards(prev => [...prev, 'Expense Request Submitted!']);
      }
      resetForm();
    } catch (err) {
      setError(err.message);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      amount: '',
      description: '',
      project: '',
      projectMission: '',
      dateSubmitted: new Date().toISOString().substring(0, 10),
      attachments: [],
    });
    setCameraImage(null);
    setOcrResult('');
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {/* Alerts */}
        <Grid item xs={12}>
          <Collapse in={!!error}>
            <Alert 
              severity="error"
              action={
                <IconButton size="small" onClick={() => setError('')}>
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          </Collapse>
          
          <Collapse in={!!success}>
            <Alert 
              severity="success"
              action={
                <IconButton size="small" onClick={() => setSuccess('')}>
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {success}
            </Alert>
          </Collapse>
        </Grid>

        {/* Basic Expense Details */}
        <Grid item xs={12}>
          <StyledPaper>
            <Typography variant="h6" sx={{ mb: 3, color: '#00BCD4', display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReceiptIcon />
              Expense Details
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Amount"
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: '$',
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                />
              </Grid>
            </Grid>
          </StyledPaper>
        </Grid>

        {/* Project Selection */}
        <Grid item xs={12}>
          <StyledPaper>
            <Typography variant="h6" sx={{ mb: 3, color: '#00BCD4' }}>
              Project Information
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Select
                  fullWidth
                  value={formData.project}
                  onChange={handleProjectChange}
                  required
                  sx={{
                    color: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.23)',
                    },
                  }}
                >
                  <MenuItem value="" disabled>Select a project</MenuItem>
                  {projects.map((proj) => (
                    <MenuItem key={proj._id} value={proj._id}>
                      {proj.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label="Project Mission"
                  multiline
                  rows={3}
                  value={formData.projectMission}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
            </Grid>
          </StyledPaper>
        </Grid>

        {/* Document Upload */}
        <Grid item xs={12}>
          <StyledPaper>
            <Typography variant="h6" sx={{ mb: 3, color: '#00BCD4' }}>
              Documentation
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  sx={{
                    color: '#00BCD4',
                    borderColor: '#00BCD4',
                    '&:hover': {
                      borderColor: '#00ACC1',
                      backgroundColor: 'rgba(0, 188, 212, 0.1)',
                    }
                  }}
                >
                  Upload Documents
                  <VisuallyHiddenInput 
                    type="file" 
                    multiple 
                    onChange={handleAttachmentChange}
                  />
                </Button>
              </Grid>

              <Grid item xs={12} md={6}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<CameraIcon />}
                  sx={{
                    color: '#00BCD4',
                    borderColor: '#00BCD4',
                    '&:hover': {
                      borderColor: '#00ACC1',
                      backgroundColor: 'rgba(0, 188, 212, 0.1)',
                    }
                  }}
                >
                  Capture Receipt
                  <VisuallyHiddenInput 
                    type="file"
                    accept="image/*"
                    onChange={handleCameraCapture}
                  />
                </Button>
              </Grid>
            </Grid>
          </StyledPaper>
        </Grid>

        {/* Progress and Rewards */}
        <Grid item xs={12}>
          <StyledPaper>
            <Typography variant="h6" sx={{ mb: 2, color: '#00BCD4' }}>
              Progress
            </Typography>
            <StyledLinearProgress variant="determinate" value={progress} />
            
            {rewards.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ color: '#00BCD4', mb: 1 }}>
                  <RewardIcon sx={{ mr: 1 }} />
                  Rewards Earned
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {rewards.map((reward, index) => (
                    <Chip
                      key={index}
                      label={reward}
                      sx={{
                        backgroundColor: 'rgba(0, 188, 212, 0.1)',
                        color: '#00BCD4',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </StyledPaper>
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#00BCD4',
              '&:hover': {
                backgroundColor: '#00ACC1',
              },
              '&:disabled': {
                backgroundColor: 'rgba(0, 188, 212, 0.5)',
              }
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Expense'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ExpenseRequestFormPro; 